import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';

/**
 * Determines whether SMS is enabled for the current event.
 *
 * Evaluates three conditions:
 * - `smsChannelEnabled` (Amplitude) — global SMS rollout flag
 * - `hasMessagingPurchase` — paid messaging plus users bypass the Amplitude flag
 * - `disableSmsUpsell` (event feature flag) — per-event override set via Event Service Center
 *
 * Logic: `(smsChannelEnabled === 'on' || hasMessagingPurchase) && !disableSmsUpsell`
 *
 * - Paid users get SMS even when the Amplitude flag is off
 * - `disableSmsUpsell` ON → SMS disabled for everyone, including paid users
 * - `disableSmsUpsell` OFF → Amplitude flag or purchase controls SMS
 *
 * @param hasMessagingPurchase - Whether the event has an active messaging plus purchase
 * @returns `[isSmsEnabled, isLoading]` tuple
 */
export const useIsSmsEnabled = (hasMessagingPurchase: boolean): [isSmsEnabled: boolean, isLoading: boolean] => {
  const { value: smsChannelValue, loading: smsChannelLoading } = useFeatureValue('smsChannelEnabled');
  const [isSmsUpsellDisabled, isSmsUpsellFlagLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'disableSmsUpsell' });

  const isLoading = smsChannelLoading || isSmsUpsellFlagLoading;
  const isSmsEnabled = (smsChannelValue === 'on' || hasMessagingPurchase) && !isSmsUpsellDisabled;

  return [isSmsEnabled, isLoading];
};
